import { useState } from "react";
import { api } from "../api";
import { useProgress } from "../progress";
import { usePoll, ErrorBanner, Loading, Stat, Section, LaneBadge, signed, when, useToast } from "./common";

function CalibChart({ buckets }) {
  if (!buckets || !buckets.length) return <div className="equity-empty">Pas encore de données de calibration</div>;
  const S = 220, pad = 24;
  const x = (v) => pad + v * (S - 2 * pad), y = (v) => S - pad - v * (S - 2 * pad);
  return (
    <svg viewBox={`0 0 ${S} ${S}`} style={{ width: "100%", maxWidth: 260, display: "block" }}>
      <line x1={pad} y1={S - pad} x2={S - pad} y2={pad} stroke="var(--line-strong)" strokeDasharray="4 4" />
      <line x1={pad} y1={S - pad} x2={S - pad} y2={S - pad} stroke="var(--line)" />
      <line x1={pad} y1={S - pad} x2={pad} y2={pad} stroke="var(--line)" />
      {buckets.map((b, i) => <circle key={i} cx={x(b.predicted)} cy={y(b.actual)} r={4 + Math.min(6, b.n)} fill="var(--accent)" opacity="0.8" />)}
      <text x={S / 2} y={S - 4} textAnchor="middle" fontSize="9" fill="var(--muted)">probabilité prédite →</text>
    </svg>
  );
}

const VERDICT = {
  ready: { cls: "badge-up", label: "Edge prouvé", hint: "Avantage statistiquement démontré — Kelly à pleine fraction." },
  promising: { cls: "badge-accent", label: "Prometteur", hint: "Signes positifs ; mises encore modérées le temps de confirmer." },
  "not-yet": { cls: "badge-warn", label: "Pas encore", hint: "Pas de preuve d'edge ; mises réduites." },
  insufficient: { cls: "", label: "Échantillon insuffisant", hint: "Trop peu de paris résolus pour conclure." },
};

function Readiness({ r }) {
  if (!r) return null;
  const v = VERDICT[r.verdict] || { cls: "", label: r.verdict, hint: "" };
  return (
    <div className="card card-pad">
      <div className="section-head"><h2>Preuve d'edge</h2><span className={"badge " + v.cls}>{v.label} · {r.score}/100</span></div>
      <div className="bar" style={{ marginBottom: 8 }}><i style={{ width: `${r.score}%` }} /></div>
      <p className="muted small">{v.hint}</p>
      {r.criteria?.length > 0 && (
        <table className="table"><tbody>
          {r.criteria.map((c) => (
            <tr key={c.key}>
              <td style={{ width: 22, color: c.pass ? "var(--up)" : "var(--muted)" }}>{c.pass ? "✓" : "○"}</td>
              <td className="small">{c.label}</td>
              <td className="mono small">{c.value}</td>
              <td className="muted tiny">cible {c.target}</td>
            </tr>
          ))}
        </tbody></table>
      )}
    </div>
  );
}

function Insight({ insight, onApplied }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  async function apply() {
    setBusy(true);
    try { await api(`/learning/insights/${insight._id}/apply`, { method: "POST" }); toast("Recommandations appliquées."); onApplied(); }
    catch (e) { toast(e.message, "down"); } finally { setBusy(false); }
  }
  return (
    <div className="card card-pad" style={{ marginBottom: 12 }}>
      <div className="row-between" style={{ marginBottom: 8 }}>
        <span className="badge">n={insight.sampleSize} · Brier {insight.brier ?? "—"}</span>
        <span className="muted tiny">{when(insight.createdAt)}</span>
      </div>
      <p className="prose">{insight.summary}</p>
      {insight.recommendations?.length > 0 && (
        <div className="table-wrap" style={{ marginBottom: 10 }}>
          <table className="table">
            <thead><tr><th>Paramètre</th><th className="right">Actuel</th><th className="right">Suggéré</th><th>Raison</th></tr></thead>
            <tbody>{insight.recommendations.map((r, i) => (
              <tr key={i}><td className="mono small">{r.param}</td><td className="right mono">{r.current}</td><td className="right mono up">{r.suggested}</td><td className="muted small">{r.reason}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {insight.recommendations?.length > 0 && (insight.applied
        ? <span className="badge badge-up">Appliqué</span>
        : <button className="btn btn-success btn-sm" disabled={busy} onClick={apply}>Appliquer les recommandations</button>)}
    </div>
  );
}

export default function Performance() {
  const { data: d, err, refresh } = usePoll(() => api("/learning"), 30000);
  const { data: dash } = usePoll(() => api("/dashboard"), 30000);
  const prog = useProgress();
  const toast = useToast();
  const [btRunning, setBtRunning] = useState(false);

  function review() { prog.run({ path: "/agents/run", body: { type: "learn" }, label: "Revue d'apprentissage", onDone: refresh, onError: (m) => toast(m, "down") }); }
  function backtest() {
    setBtRunning(true);
    prog.run({ path: "/agents/run", body: { type: "backtest" }, label: "Backtest", onDone: () => { setBtRunning(false); refresh(); }, onError: (m) => { setBtRunning(false); toast(m, "down"); } });
  }

  if (!d) return err ? <ErrorBanner err={err} /> : <Loading />;
  const c = d.calibration || {};
  const closed = d.closed || [];
  const clvs = closed.filter((b) => b.clvCents != null && !b.inPlayArtifact);
  const avgClv = clvs.length ? clvs.reduce((a, b) => a + Number(b.clvCents), 0) / clvs.length : null;
  const lanes = dash?.lanes || [];
  const b = d.backtest;

  return (
    <div>
      <div className="page-head">
        <div><h1>Performance</h1><div className="sub">A-t-on un avantage réel ? Calibration, rentabilité par voie et par catégorie, recommandations.</div></div>
        <div className="page-actions">
          <button className="btn btn-ghost btn-sm" onClick={backtest} disabled={btRunning}>{btRunning ? "Backtest…" : "Backtest du marché"}</button>
          <button className="btn btn-primary btn-sm" onClick={review}>Lancer une revue</button>
        </div>
      </div>
      <ErrorBanner err={err} />

      <div className="stat-grid">
        <Stat label="Paris IA clôturés" value={c.n || 0} sub={`${c.nResolved || 0} issues connues · analyse IA seulement, hors copies`} />
        <Stat label="P&L réalisé" value={c.realizedPnl != null ? signed(c.realizedPnl) : "—"} unit="$" cls={(c.realizedPnl || 0) >= 0 ? "up" : "down"} sub={c.voidN ? `capital ${signed(c.capitalPnl)} $ avec ${c.voidN} remboursé${c.voidN > 1 ? "s" : ""}` : "hors marchés remboursés"} />
        <Stat label="ROI" value={c.roi != null ? signed(c.roi * 100, 1) : "—"} unit="%" cls={(c.roi || 0) >= 0 ? "up" : "down"} sub="sur le capital misé" />
        <Stat label="CLV moyen" value={avgClv == null ? "—" : signed(avgClv, 1)} unit="c" cls={avgClv == null ? "" : avgClv >= 0 ? "up" : "down"} sub={`${clvs.length} derniers paris · ≥ +1c = on bat le marché`} />
        <Stat label="Brier" value={c.brier ?? "—"} sub={c.population ? `population ${c.population.brier} (n=${c.population.n}) · ${c.population.biasPts > 0 ? "surestime" : "sous-estime"} ${Math.abs(c.population.biasPts).toFixed(1)} pts` : "plus bas = mieux"} />
      </div>

      <div className="two-col section">
        <Readiness r={d.readiness} />
        <div className="card card-pad">
          <div className="section-head"><h2>Calibration</h2><span className="hint">proche de la diagonale = bien calibré</span></div>
          <div className="row" style={{ alignItems: "flex-start" }}>
            <CalibChart buckets={c.buckets} />
            {c.buckets?.length > 0 && (
              <table className="table" style={{ flex: 1 }}>
                <thead><tr><th>Prédit</th><th className="right">Réalisé</th><th className="right">n</th></tr></thead>
                <tbody>{c.buckets.map((bk, i) => <tr key={i}><td className="mono small">{bk.range}</td><td className="right mono small">{(bk.actual * 100).toFixed(0)} %</td><td className="right mono muted small">{bk.n}</td></tr>)}</tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div className="two-col section">
        <div className="card card-pad">
          <div className="section-head"><h2>Par voie</h2><span className="hint">toutes clôtures</span></div>
          <table className="table">
            <thead><tr><th>Voie</th><th className="right">n</th><th className="right">P&L</th><th className="right">ROI</th><th className="right">CLV</th></tr></thead>
            <tbody>
              {lanes.length === 0 && <tr><td colSpan={5} className="table-empty">—</td></tr>}
              {lanes.map((l) => (
                <tr key={l.lane}>
                  <td><LaneBadge lane={l.lane} /></td>
                  <td className="right mono muted">{l.all.n}</td>
                  <td className={"right mono " + (l.all.pnl >= 0 ? "up" : "down")}>{signed(l.all.pnl)} $</td>
                  <td className={"right mono " + (l.all.roi == null ? "muted" : l.all.roi >= 0 ? "up" : "down")}>{l.all.roi == null ? "—" : signed(l.all.roi, 1, " %")}</td>
                  <td className={"right mono " + (l.all.clv == null ? "muted" : l.all.clv >= 0 ? "up" : "down")}>{l.all.clv == null ? "—" : signed(l.all.clv, 1, "c")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card card-pad">
          <div className="section-head"><h2>Par catégorie</h2></div>
          {(!c.byCategory || !c.byCategory.length) && <p className="muted small">Aucune donnée pour l'instant.</p>}
          {c.byCategory?.length > 0 && (
            <table className="table">
              <thead><tr><th>Catégorie</th><th className="right">n</th><th className="right">P&L</th><th className="right">ROI</th></tr></thead>
              <tbody>{c.byCategory.map((cat, i) => (
                <tr key={i}><td className="small">{cat.category}</td><td className="right mono muted">{cat.n}</td><td className={"right mono " + (cat.pnl >= 0 ? "up" : "down")}>{signed(cat.pnl)}</td><td className={"right mono " + (cat.roi >= 0 ? "up" : "down")}>{(cat.roi * 100).toFixed(1)} %</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>

      <Section title="Revues et recommandations" hint="post-mortem périodique des paris clôturés">
        {(!d.insights || !d.insights.length) && <div className="card table-empty">Aucune revue pour l'instant.</div>}
        {d.insights?.slice(0, 6).map((ins) => <Insight key={ins._id} insight={ins} onApplied={refresh} />)}
      </Section>

      {b && (
        <Section title="Backtest du marché" hint={`le ${when(b.at)} · ${b.sample} marchés résolus, prix à ${b.horizonHours} h de la clôture`}>
          <div className="stat-grid" style={{ marginBottom: 0 }}>
            <Stat label="Brier du marché" value={b.marketBrier ?? "—"} />
            <Stat label="Notre Brier" value={b.ourBrier ?? "—"} sub={`${b.ourN || 0} paris réglés`} />
            <Stat label="On bat le marché ?" value={b.beatsMarket == null ? "—" : b.beatsMarket ? "Oui" : "Pas encore"} cls={b.beatsMarket ? "up" : b.beatsMarket === false ? "down" : ""} sub="Brier plus bas = mieux" />
          </div>
        </Section>
      )}

      {d.agents?.length > 0 && (
        <details className="fold section">
          <summary>Leçons apprises par agent ({d.agents.filter((a) => a.directives).length}/{d.agents.length})</summary>
          <div className="card table-wrap" style={{ marginTop: 10 }}>
            <table className="table">
              <thead><tr><th>Agent</th><th className="right">Révisions</th><th>Leçon</th></tr></thead>
              <tbody>{d.agents.map((a) => (
                <tr key={a.key}><td className="small">{a.label}</td><td className="right mono muted">{a.revisions}</td><td className="muted small">{a.directives || <span style={{ opacity: 0.6 }}>— pas encore —</span>}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}
